import PropTypes from 'prop-types'
import {
  Link,
  Route,
  useHistory,
  useLocation,
  useRouteMatch,
} from 'react-router-dom'
import {
  flatMapChildrenFilteredByType,
  getFieldRemoved,
  isEmpty,
  isPresent,
  noop,
} from 'utils'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import classNames from 'classnames'

const tabContext = createContext({
  tabs: [],
  activePath: '',
  setActivePath: noop,
  addTab: noop,
  removeTab: noop,
})

const TabGroupContainer = ({ children }) => {
  const { path: matchPath, url } = useRouteMatch()
  return (
    <Route path={`${matchPath}/:tabName?`}>
      <TabGroup parentPath={url}>{children}</TabGroup>
    </Route>
  )
}

TabGroupContainer.propTypes = {
  children: PropTypes.node.isRequired,
}

const TabGroup = ({ parentPath, children }) => {
  const history = useHistory()
  const { search, pathname, hash } = useLocation()
  const {
    params: { tabName },
    url,
  } = useRouteMatch()
  const [tabs, setTabs] = useState({})
  const [activePath, setActivePath] = useState(tabName)
  const subPath = (pathname + search + hash).replace(url, '')

  const addTab = useCallback(
    ({ path, title }) => {
      setTabs(_tabs => {
        if (isEmpty(_tabs) && !isPresent(activePath)) setActivePath(path)
        return {
          ..._tabs,
          [path]: title,
        }
      })
    },
    [activePath]
  )

  const removeTab = useCallback(path => setTabs(getFieldRemoved(path)), [])

  useEffect(() => {
    if (isPresent(tabs[tabName])) {
      setActivePath(tabName)
    } else {
      history.replace(
        [parentPath, activePath].filter(Boolean).join('/') + subPath
      )
    }
  }, [
    tabs,
    history,
    activePath,
    setActivePath,
    parentPath,
    tabName,
    search,
    subPath,
  ])

  return (
    <>
      <tabContext.Provider
        value={{
          tabs,
          addTab,
          removeTab,
          activePath,
          setActivePath,
        }}>
        <ul className="tab_g" role="tablist">
          {flatMapChildrenFilteredByType(children, Tab)
            .map(child => child.props)
            .map(({ path, title }) => (
              <li
                key={path}
                className={classNames({ on: activePath === path })}>
                <Link
                  to={`${parentPath}/${path}${search}`}
                  className="link_tab">
                  {title}
                </Link>
              </li>
            ))}
        </ul>
        {children}
      </tabContext.Provider>
    </>
  )
}

TabGroup.propTypes = {
  children: PropTypes.node,
  parentPath: PropTypes.string,
}

const Tab = ({ path, title, children }) => {
  const { addTab, removeTab, activePath } = useContext(tabContext)
  useEffect(() => {
    addTab({
      path,
      title,
    })
    return () => removeTab(path)
  }, [addTab, removeTab, path, title])
  return activePath === path && children
}

Tab.propTypes = {
  children: PropTypes.node.isRequired,
  path: PropTypes.string.isRequired,
  title: PropTypes.node.isRequired,
}

Tab.Group = TabGroupContainer

export default Tab
